import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { Labels } from 'src/app/labels/englishLabels';
import { Platform } from 'src/app/models/api';
import {
  EffectivePolicySettingEnum,
  CategoryToRules,
} from 'src/app/services/infra/store/model/effectivePolicySetting.model';
import { EffectivePolicySettingsColumn } from 'src/app/models/tableColumns/effectivePolicySettingsColumn.model';
import { EffectivePolicySettingsViewModel } from 'src/app/models/viewModels/effectivePolicySettingsViewModel';
import { BehaviorSubject, Observable } from 'rxjs';

export interface EffectivePolicySettingsData {
  effectivePolicySettingName: EffectivePolicySettingEnum;
  platform: Platform;
}
@Component({
  selector: 'app-effective-policy-settings-dialog',
  templateUrl: './effective-policy-settings-dialog.component.html',
  styleUrls: ['./effective-policy-settings-dialog.component.scss'],
})
export class EffectivePolicySettingsDialogComponent implements OnInit {
  // table config
  public EffectivePolicySettingsColumns = EffectivePolicySettingsColumn;
  public tableRowsMap: Map<string, Observable<BehaviorSubject<EffectivePolicySettingsViewModel>[]>>;

  public effectivePolicySettingsData: CategoryToRules[];
  private readonly _effectivePolicySettingsToCategoryToRules: Map<string, CategoryToRules[]>;

  constructor(
    public dialogRef: MatDialogRef<EffectivePolicySettingsDialogComponent>,
    private readonly _logger: LoggerService,
    @Inject(MAT_DIALOG_DATA) public data: EffectivePolicySettingsData,
    private readonly _store: StoreService
  ) {}

  ngOnInit(): void {
    this.effectivePolicySettingsData = this._store.getCategoryRulesByPlatformAndSettings(
      this.data.platform,
      this.data.effectivePolicySettingName
    );
    this._buildEffectivePolicySettingsViewModel();
  }

  public getEffectivePolicySettingsDataName() {
    switch (this.data.effectivePolicySettingName) {
      case 'allowedNumOfBuilders':
        return Labels.policyPage.config.generalSettingsContent.allowedNumberOfBuilders;
      case 'allowedConnectionsToProd':
        return Labels.policyPage.config.generalSettingsContent.allowedConnectionsToProd;
      case 'highNumberOfPrivilegedUsers':
        return Labels.policyPage.config.generalSettingsContent.highNumberOfPrivilegedUsers;
      case 'allowedConnectionsToNonProd':
        return Labels.policyPage.config.generalSettingsContent.allowedConnectionsToNonProd;
      case 'criticalNumberOfPrivilegedUsers':
        return Labels.policyPage.config.generalSettingsContent.criticalNumberOfPrivilegedUsers;
      case 'allowedNumOfPrivilegedIdentitiesInWorkspace':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesInWorkspace;
      case 'allowedNumOfPrivilegedIdentitiesToDashboard':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToDashboard;
      case 'allowedNumOfPrivilegedIdentitiesToDataset':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToDataset;
      case 'allowedNumOfPrivilegedIdentitiesToReport':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToReport;
      case 'allowedNumOfPrivilegedIdentitiesToAgent':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToAgent;
      case 'allowedNumOfPrivilegedIdentitiesToAiSkill':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToAiSkill;
      case 'allowedNumOfPrivilegedIdentitiesToGpt':
        return Labels.policyPage.config.generalSettingsContent.allowedNumOfPrivilegedIdentitiesToGpt;
      case 'allowedMemoryRetentionInDays':
        return Labels.policyPage.config.generalSettingsContent.allowedMemoryRetentionInDays;
      case 'allowedMemoryMaxRecentSessions':
        return Labels.policyPage.config.generalSettingsContent.allowedMemoryMaxRecentSessions;
      case 'allowedIdentityProviders':
        return Labels.policyPage.config.generalSettingsContent.allowedIdentityProviders;
      case 'domainsList':
        return Labels.policyPage.config.trustedDomainsContent.domainsList;
      case 'connectorList':
        return this.data.effectivePolicySettingName;
      case 'endpoints':
        return this.data.effectivePolicySettingName;
      default:
        this._logger.error(':effective policy setting name not found' + this.data.effectivePolicySettingName);
        return this.data.effectivePolicySettingName;
    }
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public getEffectivePolicySettingsRulesByCAtegory(
    category: string
  ): Observable<BehaviorSubject<EffectivePolicySettingsViewModel>[]> {
    return this.tableRowsMap.get(category) || new Observable<BehaviorSubject<EffectivePolicySettingsViewModel>[]>();
  }

  private _buildEffectivePolicySettingsViewModel() {
    this.tableRowsMap = new Map<string, Observable<BehaviorSubject<EffectivePolicySettingsViewModel>[]>>();
    this.effectivePolicySettingsData.forEach((categoryToRule) => {
      const viewModels: BehaviorSubject<EffectivePolicySettingsViewModel>[] = [];
      categoryToRule.rulesSummary.forEach((rule) => {
        const effectivePolicySettingsViewModel: EffectivePolicySettingsViewModel = {
          ruleId: rule.id,
          ruleName: rule.displayName,
          rowId: rule.id,
          clickable: false,
          tooltipDynamicText: rule.displayName,
        } as EffectivePolicySettingsViewModel;
        viewModels.push(new BehaviorSubject(effectivePolicySettingsViewModel));
      });

      this.tableRowsMap.set(categoryToRule.category, new BehaviorSubject(viewModels));
    });
  }
}
